import {
  RECOMMENDATION_SELECTION_TOKEN_TYPE,
  RECOMMENDATION_SELECTION_TOKEN_VERSION,
} from '@optiflow/types';

/**
 * Where a basket line came from, when it came from a frame recommendation.
 *
 * The token is the only part checkout trusts, and it verifies it again. The rest are copies of the token's own claims, read here without verifying anything, for two jobs only: showing the Recommended badge and its reasons, and deciding whether two lines of the same frame are the same recommendation. No price, stock, score, rank, filter or face shape is ever in it.
 */
export interface LineRecommendation {
  selectionToken: string;
  customerId: string;
  ruleVersion: string;
  faceAssessmentId: string | null;
  /** Unique and sorted, as the token carries them. */
  reasonCodes: string[];
}

/** The claims of a selection token as the browser can read them. */
export interface SelectionClaims {
  customerId: string;
  variantId: string;
  ruleVersion: string;
  faceAssessmentId: string | null;
  reasonCodes: string[];
}

const TOKEN_PART = /^[A-Za-z0-9_-]+$/;
/** Longer than any token the server signs, as checkout also refuses. */
const MAX_TOKEN_LENGTH = 2048;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

function decodeBase64Url(part: string): string {
  const base64 = part.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  return new TextDecoder().decode(Uint8Array.from(binary, (char) => char.charCodeAt(0)));
}

/** Reads a token's claims, or null when it is not shaped like a selection token. Reading is not verifying: a forged token reads just as well, and only checkout can tell. */
export function readSelectionClaims(token: unknown): SelectionClaims | null {
  if (typeof token !== 'string' || token.length === 0 || token.length > MAX_TOKEN_LENGTH) {
    return null;
  }
  const parts = token.split('.');
  if (parts.length !== 3 || !parts.every((part) => TOKEN_PART.test(part))) return null;

  let payload: unknown;
  try {
    payload = JSON.parse(decodeBase64Url(parts[1] ?? ''));
  } catch {
    return null;
  }
  if (!isRecord(payload)) return null;
  if (payload.typ !== RECOMMENDATION_SELECTION_TOKEN_TYPE) return null;
  if (payload.v !== RECOMMENDATION_SELECTION_TOKEN_VERSION) return null;

  const { cid, vid, rv, fa, rc } = payload;
  if (typeof cid !== 'string' || cid === '' || typeof vid !== 'string' || vid === '') return null;
  if (typeof rv !== 'string' || rv.trim() === '') return null;
  if (fa !== null && (typeof fa !== 'string' || fa === '')) return null;
  if (!Array.isArray(rc) || !rc.every((code): code is string => typeof code === 'string')) {
    return null;
  }

  return {
    customerId: cid,
    variantId: vid,
    ruleVersion: rv,
    faceAssessmentId: fa,
    reasonCodes: rc,
  };
}

/** A line's recommendation built from its token, or null when the token cannot be read. The copies always come from the token, never from anywhere else. */
export function recommendationFromToken(selectionToken: unknown): LineRecommendation | null {
  const claims = readSelectionClaims(selectionToken);
  if (!claims || typeof selectionToken !== 'string') return null;
  return {
    selectionToken,
    customerId: claims.customerId,
    ruleVersion: claims.ruleVersion,
    faceAssessmentId: claims.faceAssessmentId,
    reasonCodes: [...claims.reasonCodes],
  };
}

/**
 * Whether two lines of the same frame may be merged into one.
 *
 * Two lines sold by hand may. A recommended line and a line sold by hand never may. Two recommended lines may only when their recommendations are the same: the same customer, rules, face assessment and reasons. The raw tokens are never compared, because the same recommendation signed a second apart is a different string.
 */
export function sameRecommendation(
  a: LineRecommendation | null,
  b: LineRecommendation | null,
): boolean {
  if (a === null || b === null) return a === b;
  const codesA = [...a.reasonCodes].sort();
  const codesB = [...b.reasonCodes].sort();
  return (
    a.customerId === b.customerId &&
    a.ruleVersion === b.ruleVersion &&
    a.faceAssessmentId === b.faceAssessmentId &&
    codesA.length === codesB.length &&
    codesA.every((code, index) => code === codesB[index])
  );
}
